import type { VehicleKind, VehicleStats } from './types';

export const TILE = 32;
export const MAP_W = 80;
export const MAP_H = 60;

export const WORLD_W = MAP_W * TILE;
export const WORLD_H = MAP_H * TILE;

export const PLAYER = {
  walkSpeed: 140,
  health: 100,
  punchRange: 28,
  punchDamage: 25,
  punchCooldownMs: 350,
  enterRange: 36
};

export const WANTED = {
  max: 5,
  decayPerSec: 0.05, // ~20s per star
  hitPed: 1,
  killPed: 2,
  stealOccupied: 1,
  hitCop: 3,
  destroyCop: 2,
  copSightRange: 360
};

export const VEHICLE_STATS: Record<VehicleKind, VehicleStats> = {
  Hatchback: {
    maxSpeed: 240, accel: 360, turn: 2.6, hp: 100,
    width: 20, height: 34, scale: 0.55,
    textures: ['veh_hatch_red', 'veh_hatch_green', 'veh_hatch_yellow']
  },
  TaxiBlack: {
    maxSpeed: 220, accel: 320, turn: 2.4, hp: 120,
    width: 20, height: 34, scale: 0.55,
    textures: ['veh_taxi']
  },
  PoliceCar: {
    maxSpeed: 280, accel: 420, turn: 2.8, hp: 140,
    width: 20, height: 34, scale: 0.55,
    textures: ['veh_police']
  }
};

/**
 * Each bark = { text: shown in bubble, say: respelled phonetically so the en-GB TTS
 * voice approximates a Scouse accent (h-dropping, glottal stops, high-rising terminal).
 * If `say` is omitted, `text` is used unchanged.
 */
export interface Bark { text: string; say?: string }

export const PED_BARKS: Bark[] = [
  // Greetings
  { text: "Y'alright lad",     say: 'yawright lahd' },
  { text: "Y'alright queen",   say: 'yawright kween' },
  { text: "Y'alright la",      say: 'yawright laaa' },
  { text: 'Sound mate',        say: 'sount may' },
  { text: 'How do',            say: 'ow do' },
  { text: 'Eh eh',             say: 'eyy eyy' },
  { text: 'Ay up',             say: 'eye oop' },

  // Approvals
  { text: 'Boss that',         say: 'boss tharrr' },
  { text: 'Sound that',        say: 'sount tharrr' },
  { text: 'Made up for ya',    say: 'made oop fer yer' },
  { text: 'Belter',            say: 'beltahh' },
  { text: 'Cracking that',     say: 'crackn tharrr' },
  { text: 'Norra problem',     say: 'norra problem' },
  { text: 'Sweet',             say: 'sweehht' },

  // Mild scolds / warnings
  { text: 'Calm down soft lad', say: 'carrm down sofft lahd' },
  { text: 'Behave!',           say: 'bee-haave' },
  { text: 'Be told!',          say: 'bee told' },
  { text: 'Watch the kecks',   say: 'watch duh kex' },
  { text: 'Pack it in',        say: 'pack it n' },
  { text: 'Gerroff',           say: 'gerr-off' },
  { text: 'Eh, watch it',      say: 'eyy, watch it' },
  { text: "Don't start",       say: 'doan staart' },

  // Asks / complaints
  { text: 'I want some chicken and a can of coke',
    say:  'I wahnt sum chickn and a cahn of coke' },
  { text: 'Gizza ciggy',       say: 'gizza siggee' },
  { text: 'Gorra spare quid?', say: 'gorra spehr kwidd' },
  { text: "Where's the bizzies?", say: 'wairs duh bizzeez' },
  { text: 'Brassick me',       say: 'brass-ick mee' },
  { text: 'Place is chocka',   say: 'place is chock-ahh' },
  { text: 'Eee gorra giro',    say: 'eee gorra jye-roh' },

  // Filler / farewells
  { text: 'Tarra',             say: 'tarrah' },
  // (filler continues below; aggro pool is exported separately)
  { text: 'Cheers la',         say: 'cheers laaa' },
  { text: 'Ta queen',          say: 'taahh kween' },
  { text: 'Ay yer ma',         say: 'eye yer mahh' },
  { text: 'Wha?',              say: 'whaa' },
  { text: 'Geddon',            say: 'geddonnn' }
];

/** Aggressive barks fired when a pedestrian is hit but still alive. */
export const AGGRO_BARKS: Bark[] = [
  { text: "Yer dead, lad!",                say: 'yer ded lahd' },
  { text: "I'll smash yer 'ead in!",       say: 'all smahsh yer ed in' },
  { text: "Try that again, sunbeam!",      say: 'try thaht uh-gen sunbeam' },
  { text: "Yer havin' a laugh?!",          say: 'yer avven a laaarf' },
  { text: "Get round 'ere, soft lad!",     say: 'geh rownd ere sofft lahd' }
];

export const DEV = { showFps: true, debugPhysics: false };
